import { useMemo } from "react";

import { Header, Link } from "components/elements";
import { Column, Row, Section, Subsection } from "components/layout";
import { TiVoAppProvider } from "hooks/tivoApps/types";

import { IMAGE_ENVS, IMAGE_LABELS, IMAGES } from "../../constants";
import { IMAGE_SUBSECTION_HEADERS, TEXTS } from "../constants";
import styles from "./ImagesSection.scss";

type ImageGroup = {
  header: string;
  images: {
    name: IMAGES;
    label: string;
    url: string;
  }[];
};

export type ImagesSectionProps = {
  assets: TiVoAppProvider["assets"];
};

type EnvAssets = TiVoAppProvider["assets"]["prod" | "staging"];

function getImageGroups(envAssets: EnvAssets): ImageGroup[] {
  if (!envAssets) {
    return [];
  }

  return Object.values(IMAGES)
    .reduce<ImageGroup[]>(
      (groups, imageName) => {
        const imageAsset = envAssets[imageName];
        if (!imageAsset) {
          return groups;
        }
        let groupHeader = IMAGE_SUBSECTION_HEADERS["*"];
        for (const [key, header] of Object.entries(IMAGE_SUBSECTION_HEADERS)) {
          if (key === "*") {
            continue;
          }
          if (imageName.toLowerCase().includes(key)) {
            groupHeader = header;
            break;
          }
        }
        groups
          .find(({ header }) => header === groupHeader)
          ?.images.push({
            name: imageName,
            label: IMAGE_LABELS[imageName],
            url: imageAsset.url,
          });

        return groups;
      },
      [
        IMAGE_SUBSECTION_HEADERS.square,
        IMAGE_SUBSECTION_HEADERS.rectangle,
        IMAGE_SUBSECTION_HEADERS.wide,
        IMAGE_SUBSECTION_HEADERS["*"],
      ].map((header) => ({ header, images: [] }))
    )
    .filter(({ images }) => images.length > 0);
}

function ImageColumn({
  title,
  imageGroups,
}: {
  title: string;
  imageGroups: ImageGroup[];
}) {
  if (imageGroups.length === 0) {
    return null;
  }

  return (
    <Column>
      <Header bold uppercase color="secondary" type="h4">
        {title}
      </Header>
      {imageGroups.map(({ header, images }) => (
        <Subsection key={header} className={styles.subsection} header={header}>
          {images.map(({ name, label, url }) => (
            <div key={name} className={styles.image}>
              <p className={styles.label}>{label}</p>
              <Link className={styles.preview} href={url} target="_blank">
                <img alt={label} src={url} />
              </Link>
            </div>
          ))}
        </Subsection>
      ))}
    </Column>
  );
}

export function ImagesSection({ assets }: ImagesSectionProps) {
  const stagingImages = useMemo(
    () => getImageGroups(assets?.staging),
    [assets?.staging]
  );
  const prodImages = useMemo(
    () => getImageGroups(assets?.prod),
    [assets?.prod]
  );

  if (stagingImages.length === 0 && prodImages.length === 0) {
    return (
      <Section header={TEXTS.SHARED.IMAGES_SECTION_HEADER}>
        <p className={styles.message}>{TEXTS.SHARED.NO_IMAGES_MESSAGE}</p>
      </Section>
    );
  }

  return (
    <Section header={TEXTS.SHARED.IMAGES_SECTION_HEADER}>
      <Row>
        <ImageColumn title={IMAGE_ENVS.STAGING} imageGroups={stagingImages} />
        <ImageColumn title={IMAGE_ENVS.PROD} imageGroups={prodImages} />
      </Row>
    </Section>
  );
}
